import * as T from 'three';
// A living face for toys with eyes: they blink now and then, squeeze shut
// while the toy is pressed, and pop open a little wide when it is let go.
// Very rarely a good squeeze leaves heart eyes for a moment.
// The eyes are squashed by a matrix about their current centre, across the
// face, so the soft-body deformation underneath stays as it is.

// GLTFLoader drops the dot from Blender names: face_eye.001 arrives as face_eye001.
const EYE=/^face_eyes?(\.?\d+)?$/,GLINT=/^face_glints?(\.?\d+)?$/;

// parts: [{name, rest}] in toy space. Returns the eyes with their centre and
// axes, and each glint with the index of its nearest eye, or null. Eyes are
// closed shapes, so their normals average out to nothing; faces here stand
// upright, so the lid closes along the toy's up axis, square to the line
// between the eyes.
// center: the middle of the toy, so each eye knows which way is out of the face.
export function findFaceParts(parts,center=null){
  const centre=rest=>{const box=new T.Box3();for(let i=0;i<rest.length;i+=3)box.expandByPoint(new T.Vector3(rest[i],rest[i+1],rest[i+2]));return box.getCenter(new T.Vector3());};
  const eyes=parts.map((p,index)=>({...p,index})).filter(p=>EYE.test(p.name)).map(p=>({name:p.name,index:p.index,center:centre(p.rest)}));
  if(!eyes.length)return null;
  for(const eye of eyes){
    // Across the face: toward the nearest other eye, or sideways for a single mesh holding both.
    const other=eyes.filter(e=>e!==eye).sort((a,b)=>a.center.distanceTo(eye.center)-b.center.distanceTo(eye.center))[0];
    const across=other&&other.center.distanceTo(eye.center)>1e-6?other.center.clone().sub(eye.center).normalize():new T.Vector3(1,0,0);
    const up=new T.Vector3(0,1,0).addScaledVector(across,-across.y);
    eye.across=across;eye.up=up.lengthSq()>1e-6?up.normalize():new T.Vector3(0,0,1);
    // Out of the face, and the eye's extent along each axis.
    const out=new T.Vector3().crossVectors(across,eye.up).normalize(),away=center?eye.center.clone().sub(center):new T.Vector3(0,0,1);
    if(out.dot(away)<0)out.negate();eye.out=out;
    const rest=parts[eye.index].rest,extent=[0,0,0],axes=[across,eye.up,out],v=new T.Vector3();
    for(let i=0;i<rest.length;i+=3){v.set(rest[i],rest[i+1],rest[i+2]).sub(eye.center);axes.forEach((a,k)=>{extent[k]=Math.max(extent[k],Math.abs(v.dot(a)));});}
    eye.size={across:extent[0]*2,up:extent[1]*2,depth:extent[2]*2};
  }
  const glints=parts.map((p,index)=>({name:p.name,index,rest:p.rest})).filter(p=>GLINT.test(p.name)).map(p=>{
    const c=centre(p.rest);return {name:p.name,index:p.index,eye:eyes.reduce((best,e,i)=>e.center.distanceTo(c)<eyes[best].center.distanceTo(c)?i:best,0)};
  });
  return {eyes,glints};
}

// A heart, one unit wide and deep, centred on the origin, facing +z.
export function heartGeometry(){
  const s=new T.Shape();
  s.moveTo(0,-.42);
  s.bezierCurveTo(-.08,-.3,-.5,-.06,-.5,.18);s.bezierCurveTo(-.5,.38,-.33,.48,-.24,.48);s.bezierCurveTo(-.11,.48,-.03,.4,0,.3);
  s.bezierCurveTo(.03,.4,.11,.48,.24,.48);s.bezierCurveTo(.33,.48,.5,.38,.5,.18);s.bezierCurveTo(.5,-.06,.08,-.3,0,-.42);
  const g=new T.ExtrudeGeometry(s,{depth:.5,bevelEnabled:true,bevelThickness:.25,bevelSize:.07,bevelSegments:4,curveSegments:14});
  g.center();g.computeBoundingBox();const size=g.boundingBox.getSize(new T.Vector3());
  g.scale(1/size.x,1/size.x,1/size.z);g.computeVertexNormals();return g;
}

export class ToyFace{
  constructor(parts,{random=Math.random,reducedMotion=false,center=null,heartChance=.04,heartRest=90,heartFor=1.8}={}){
    this.parts=findFaceParts(parts,center);this.random=random;this.reducedMotion=reducedMotion;
    Object.assign(this,{heartChance,heartRest,heartFor});
    this.openness=1;this.speed=0;this.clock=0;this.held=false;this.release=false;
    this.heart=0;this.heartSpeed=0;this.heartUntil=-1;this.lastHeart=-Infinity;this.pressed=false;this.pressStart=0;
    this.blinkAt=this.nextBlink(1);this.blinkEnd=-1;
    this.basis=new T.Matrix4();this.shift=new T.Matrix4();this.out=new T.Matrix4();this.heartOut=new T.Matrix4();
  }
  nextBlink(after){return this.clock+after+1.2+3.4*this.random();}
  // Shut the eyes for a moment, as at a sudden pop.
  flinch(seconds){this.flinchUntil=this.clock+seconds;}
  // Heart eyes for a moment.
  adore(seconds=this.heartFor){this.heartUntil=this.clock+seconds;this.lastHeart=this.clock;}
  get loving(){return this.clock<this.heartUntil;}
  get glintVisible(){return this.openness>.45&&this.heart<.05;}
  // held: the toy is being pressed. Returns true while the lids are moving.
  step(dt,held){
    if(!this.parts||!(dt>0))return false;
    // Rarely, letting go of a real squeeze (not a tap) brings heart eyes.
    if(held&&!this.pressed)this.pressStart=this.clock;
    if(!held&&this.pressed&&this.clock-this.pressStart>=.35&&this.clock-this.lastHeart>=this.heartRest&&this.random()<this.heartChance)this.adore();
    this.pressed=held;
    held=held||this.clock<(this.flinchUntil??0)||this.loving;
    if(held&&!this.held)this.release=false;
    if(!held&&this.held){this.release=!this.reducedMotion;this.blinkAt=this.nextBlink(.6);}
    this.held=held;
    let target=1,omega=30,zeta=1;
    if(held){target=.05;omega=55;}
    else{
      if(this.clock>=this.blinkAt){
        this.blinkEnd=this.clock+.08;
        // Now and then a quick double blink.
        this.blinkAt=this.random()<.22?this.clock+.26:this.nextBlink(0);
      }
      if(this.clock<this.blinkEnd){target=.05;omega=55;this.release=false;}
      else if(this.release){omega=18;zeta=.55;}
    }
    let left=dt;
    while(left>1e-9){
      const h=Math.min(left,1/240);
      this.speed+=(omega*omega*(target-this.openness)-2*zeta*omega*this.speed)*h;this.openness+=this.speed*h;left-=h;
    }
    // The heart pops in a little past full size, and shrinks away when done.
    const heartTarget=this.loving?1:0,ho=this.loving?20:30,hz=this.loving&&!this.reducedMotion?.45:1;
    left=dt;
    while(left>1e-9){
      const h=Math.min(left,1/240);
      this.heartSpeed+=(ho*ho*(heartTarget-this.heart)-2*hz*ho*this.heartSpeed)*h;this.heart=Math.max(0,this.heart+this.heartSpeed*h);left-=h;
    }
    const heartMoving=Math.abs(this.heartSpeed)>.002||Math.abs(heartTarget-this.heart)>.002;
    if(!heartMoving){this.heart=heartTarget;this.heartSpeed=0;}
    this.clock+=dt;
    if(this.release&&Math.abs(this.openness-1)<.002&&Math.abs(this.speed)<.01)this.release=false;
    const moving=Math.abs(this.speed)>.002||Math.abs(target-this.openness)>.002;
    if(!moving&&!held&&this.clock>=this.blinkEnd){this.openness=target;this.speed=0;}
    return moving||heartMoving;
  }
  // The pose of eye i (and its glints), squashed about where the eye is now.
  matrix(i,center){
    const eye=this.parts.eyes[i],k=Math.max(.04,this.openness),wide=1+.06*Math.max(0,1-k);
    // Under a heart the eye shrinks to a point and is not drawn: squashed to a
    // line it is so steep that its depth offset would put it over the heart.
    if(this.heart>.5)return this.out.makeScale(0,0,0).setPosition(center);
    const u=eye.up,x=eye.across,e=this.basis.elements;
    // I + (k-1) u u^T + (wide-1) x x^T, column-major.
    const s=[[u.x,u.y,u.z],[x.x,x.y,x.z]];
    this.basis.identity();
    for(let c=0;c<3;c++)for(let r=0;r<3;r++)e[c*4+r]+=(k-1)*s[0][r]*s[0][c]+(wide-1)*s[1][r]*s[1][c];
    this.shift.makeTranslation(-center.x,-center.y,-center.z);
    return this.out.makeTranslation(center.x,center.y,center.z).multiply(this.basis).multiply(this.shift);
  }
  // The pose of eye i's heart: on the eye's centre, upright, facing out of the
  // face, a little wider than the eye and as deep, scaled by how far it has popped in.
  heartMatrix(i,center){
    const eye=this.parts.eyes[i],x=new T.Vector3().crossVectors(eye.up,eye.out).normalize(),k=this.heart;
    const width=Math.max(eye.size.across,eye.size.up)*1.3,depth=Math.max(eye.size.depth,width*.3);
    this.heartOut.makeBasis(x,eye.up,eye.out).scale(new T.Vector3(width*k,width*k,depth*k));
    const at=center.clone().addScaledVector(eye.out,depth*.15);
    return this.heartOut.setPosition(at);
  }
}
